import {
  STRATEGIES,
  fetchAndParseCSV,
  computeMetrics,
  aggregateDaily,
} from "./portfolioBuilderLogic";

// Shared loader for every marketing surface that displays full-portfolio
// backtest numbers. Keeping one cache here means the thank-you snapshot and
// the landing-page heatmap can never show two different versions of the same
// data set, and a single page visit never fetches the PB CSVs twice.

export const STARTING_CAPITAL = 25000;

export function fmtUSD(n, { compact = false } = {}) {
  if (n == null || !isFinite(n)) return "—";
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  if (compact && abs >= 1000) {
    return `${sign}$${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}k`;
  }
  return `${sign}$${abs.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

let tradesCache = null;
let tradesPromise = null;

// Merged, chronologically sorted trades across all active PB strategies.
export async function loadPortfolioTrades() {
  if (tradesCache) return tradesCache;
  if (tradesPromise) return tradesPromise;

  tradesPromise = (async () => {
    const results = await Promise.all(
      STRATEGIES.map(async (s) => {
        const { trades, error } = await fetchAndParseCSV(s.file, { pyramid: !!s.pyramid });
        if (error) {
          // Soft-fail per strategy so one bad CSV doesn't take down the page.
          // eslint-disable-next-line no-console
          console.warn(`[portfolioSnapshot] ${s.id} failed: ${error}`);
          return [];
        }
        return trades.map((t) => ({ ...t, strategyId: s.id }));
      })
    );

    const merged = results
      .flat()
      .filter((t) => t && t.exit_time)
      .sort((a, b) => a.exit_time - b.exit_time);

    if (!merged.length) {
      throw new Error("No trades loaded from PB CSVs");
    }

    tradesCache = merged;
    return merged;
  })();

  try {
    return await tradesPromise;
  } catch (e) {
    tradesPromise = null;
    throw e;
  }
}

let metricsCache = null;
let metricsPromise = null;

// computeMetrics runs a 5,000-path Monte Carlo, so every surface shares one
// result rather than recomputing it per component.
export async function loadPortfolioMetrics() {
  if (metricsCache) return metricsCache;
  if (metricsPromise) return metricsPromise;

  metricsPromise = (async () => {
    const merged = await loadPortfolioTrades();
    const daily = aggregateDaily(merged);
    metricsCache = { merged, daily, metrics: computeMetrics(merged, daily) };
    return metricsCache;
  })();

  try {
    return await metricsPromise;
  } catch (e) {
    metricsPromise = null;
    throw e;
  }
}

// Portfolio Builder's Minimum Capital Required for the same portfolio, using
// PB's own floor and rounding so the landing pages and PB never quote two
// different starting balances.
export function minimumCapitalFor(metrics) {
  return Math.max(1000, Math.round(metrics.minCapitalRequired));
}

let monthlyCache = null;
let monthlyPromise = null;

// Monthly PnL indexed as { 'YYYY-MM': totalPnl }, plus the starting balance
// the heatmap should be measured against.
export async function loadMonthlyReturns() {
  if (monthlyCache) return monthlyCache;
  if (monthlyPromise) return monthlyPromise;

  monthlyPromise = (async () => {
    const { merged, daily, metrics } = await loadPortfolioMetrics();

    const monthlyMap = {};
    for (const d of daily) {
      const m = d.date.slice(0, 7);
      monthlyMap[m] = (monthlyMap[m] || 0) + d.pnl;
    }

    monthlyCache = {
      monthlyMap,
      firstDate: merged[0].date,
      lastDate: merged[merged.length - 1].date,
      tradeCount: merged.length,
      minimumCapital: minimumCapitalFor(metrics),
    };
    return monthlyCache;
  })();

  try {
    return await monthlyPromise;
  } catch (e) {
    monthlyPromise = null;
    throw e;
  }
}

let snapshotCache = null;
let snapshotPromise = null;

export async function loadPortfolioSnapshot() {
  if (snapshotCache) return snapshotCache;
  if (snapshotPromise) return snapshotPromise;

  snapshotPromise = (async () => {
    const { merged, daily, metrics } = await loadPortfolioMetrics();
    const { monthlyMap, firstDate, lastDate, minimumCapital } = await loadMonthlyReturns();

    // Build a {date, equity} series for the chart, down-sampled to ~400
    // points so recharts stays smooth with 11K+ trades.
    let runningEq = 0;
    const fullSeries = merged.map((t) => {
      runningEq += t.pnl;
      return { date: t.date, equity: runningEq };
    });
    const targetPoints = 400;
    const step = Math.max(1, Math.floor(fullSeries.length / targetPoints));
    const series = [];
    for (let i = 0; i < fullSeries.length; i += step) {
      series.push(fullSeries[i]);
    }
    // Always keep the last data point so the chart's end matches totalPnl.
    if (series[series.length - 1] !== fullSeries[fullSeries.length - 1]) {
      series.push(fullSeries[fullSeries.length - 1]);
    }

    // Portfolio max drawdown on the END-OF-DAY equity curve. Match PB's
    // computeMaxDrawdown(dailyData) exactly so the number on this page is
    // identical to what Portfolio Builder reports — otherwise sales reps
    // and prospects see two different "max DD" values from the same data.
    // Trade-by-trade DD would be larger (intraday wiggles count); end-of-
    // day is the canonical number we cite.
    let peakDaily = 0;
    let cumDaily = 0;
    let maxDD = 0;
    for (const d of daily) {
      cumDaily += d.pnl;
      if (cumDaily > peakDaily) peakDaily = cumDaily;
      const dd = peakDaily - cumDaily;
      if (dd > maxDD) maxDD = dd;
    }
    const ddPctOfPeak = peakDaily > 0 ? maxDD / peakDaily : 0;

    // CAGR uses the same minimum-capital basis as the home / demo equity
    // comparison and monthly heatmap so confirmation-page metrics match.
    let monthlyTotal = 0;
    for (const k of Object.keys(monthlyMap)) monthlyTotal += monthlyMap[k];
    const cagrStart = minimumCapital || STARTING_CAPITAL;
    const endBalanceForCagr = cagrStart + monthlyTotal;
    const startMs = new Date(firstDate + "T00:00:00").getTime();
    const endMs = new Date(lastDate + "T00:00:00").getTime();
    const yearsElapsed = (endMs - startMs) / (1000 * 60 * 60 * 24 * 365.25);
    const cagr =
      yearsElapsed > 0 && endBalanceForCagr > 0
        ? Math.pow(endBalanceForCagr / cagrStart, 1 / yearsElapsed) - 1
        : 0;

    snapshotCache = {
      metrics,
      series,
      maxDD,
      ddPctOfPeak,
      cagr,
      monthlyMap,
      minimumCapital,
      firstDate,
      lastDate,
      strategyCount: STRATEGIES.length,
      tradeCount: merged.length,
    };
    return snapshotCache;
  })();

  try {
    return await snapshotPromise;
  } catch (e) {
    snapshotPromise = null;
    throw e;
  }
}
